import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  CardHeader,
  Collapse,
  Button,
  Table,
  Progress
} from "reactstrap";
import classnames from "classnames";
import { useParams, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useProfile } from "../../../Components/Hooks/UserHooks";
import { getBlockById as onGetBlockDetails } from "../../../slices/thunks";
import moment from "moment";
import './css/BlockDetail.css'

// Component for displaying info in a table
const InfoTable = ({ data }) => (
  <Table className="custom-table">
    <tbody>
      {data.map((item, index) => (
        <tr key={index}>
          <th style={{ paddingLeft: 0, width: "40%" }}>{item.label}</th>
          <td className="text-muted">{item.value}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

// Component for section headers with icons
const SectionHeader = ({ icon, title, color }) => (
  <div className="d-flex align-items-center">
    <span
      className="text-white shadow-sm section-header-icon"
      style={{ backgroundColor: color }}
    >
      <i className={`${icon} fs-5`}></i>
    </span>
    <h5 className="mb-0">{title}</h5>
  </div>
);

// Component for metric cards
const MetricCard = ({ icon, title, value, subtitle, color, bgColor }) => (
  <div className="metric-card" style={{ backgroundColor: bgColor, borderRadius: "12px" }}>
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="metric-icon" style={{ backgroundColor: color }}>
          <i className={`${icon} text-white`}></i>
        </div>
        <div className="text-end">
          <div className="metric-value" style={{ color }}>{value}</div>
          <div className="metric-subtitle">{subtitle}</div>
        </div>
      </div>
      <h6 className="metric-title mb-0">{title}</h6>
    </div>
  </div>
);

// Loading component
const LoadingState = ({ color }) => (
  <div className="page-content">
    <Container fluid>
      <div className="text-center mt-5">
        <div className="spinner-grow" style={{ color, width: "3rem", height: "3rem" }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 fs-5">Loading block details...</p>
      </div>
    </Container>
  </div>
);

// Error component
const ErrorState = ({ error, color, onRetry }) => (
  <div className="page-content">
    <Container fluid>
      <div className="text-center mt-5">
        <div className="alert alert-danger p-4 shadow-sm border-0" style={{ borderRadius: "12px" }}>
          <i className="ri-error-warning-line fs-1 d-block mb-3"></i>
          <h4 className="mb-3">Failed to load block details</h4>
          <p className="mb-4">{error}</p>
          <Button
            style={{ 
              backgroundColor: color, 
              borderColor: color, 
              padding: "10px 24px",
              borderRadius: "8px" 
            }}
            onClick={onRetry}
          >
            <i className="ri-refresh-line me-1"></i> Retry
          </Button>
        </div>
      </div>
    </Container>
  </div>
);

// Main card component
const StyledCard = ({ children, className = "" }) => (
  <Card
    className={`${className} border-0 shadow-sm card-hover`}
    style={{ borderRadius: "16px", transition: "all 0.3s ease" }}
  >
    {children}
  </Card>
);

const BlockDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [block, setBlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [col1, setcol1] = useState(true);

  // Colors
  const colors = {
    primary: "#6366f1",
    secondary: "#8b5cf6", 
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    info: "#06b6d4",
    dark: "#1f2937"
  };

  const fetchBlockData = useCallback(() => {
    if (id) {
      setLoading(true);
      dispatch(onGetBlockDetails(id))
        .unwrap()
        .then((data) => {
          setBlock(data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to fetch block details:", error);
          setError(error.message || "Failed to load block details");
          setLoading(false);
        });
    }
  }, [id, dispatch]);

  useEffect(() => {
    fetchBlockData();
  }, [fetchBlockData]);

  // Format the date using moment.js
  const formatDate = (date) => {
    return date ? moment(date).format("DD MMM YYYY") : "N/A";
  };

  function togglecol1() {
    setcol1(!col1);
  }

  if (loading) {
    return <LoadingState color={colors.primary} />;
  }

  if (error) {
    return <ErrorState error={error} color={colors.primary} onRetry={() => window.location.reload()} />;
  }

  // Calculate metrics
  const totalArea = (block?.total_terrace_surface || 0) + (block?.total_apartment_surface || 0);
  const avgFloorArea = block?.total_floors > 0 ? Math.round(totalArea / block.total_floors) : 0;

  // Prepare data for basic information table
  const basicInfoData = [
    { label: "Block ID", value: block?.id || "N/A" },
    { label: "Phase", value: block?.phase || "N/A" },
    { label: "Description", value: block?.description || "N/A" },
    { label: "Created Date", value: formatDate(block?.created_at) }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Block Details" pageTitle="Blocks" />

        {/* Header Section */}
        <Row className="mb-4">
          <Col>
            <StyledCard className="hero-card">
              <CardBody className="p-4">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h2 className="hero-title mb-2">Block {block?.name}</h2>
                    <p className="hero-subtitle mb-0">
                      <i className="ri-map-pin-line me-2"></i>
                      Phase {block?.phase} • {block?.total_flats} Units • {totalArea.toLocaleString()} sqm
                    </p>
                  </div>
                  <div className="text-end">
                    <div className={`status-badge ${block?.active ? 'active' : 'inactive'}`}>
                      <i className={`ri-${block?.active ? 'check' : 'close'}-circle-line me-1`}></i>
                      {block?.active ? 'Active' : 'Inactive'}
                    </div>
                    <div className="hero-price mt-2">
                      €{new Intl.NumberFormat('en-US').format(block?.settled_price || 0)}
                    </div>
                  </div>
                </div>
              </CardBody>
            </StyledCard>
          </Col>
        </Row>

        {/* Metrics Section */}
        <Row className="g-4 mb-4">
          <Col lg={3} sm={6}>
            <MetricCard
              icon="ri-home-4-line"
              title="Total Flats"
              value={block?.total_flats || 0}
              subtitle="residential units"
              color={colors.primary}
              bgColor="#f0f9ff"
            />
          </Col>
          <Col lg={3} sm={6}>
            <MetricCard
              icon="ri-building-line"
              title="Total Floors"
              value={block?.total_floors || 0}
              subtitle={`${avgFloorArea} sqm avg`}
              color={colors.success}
              bgColor="#f0fdf4"
            />
          </Col>
          <Col lg={3} sm={6}>
            <MetricCard
              icon="ri-rulers-line"
              title="Total Area"
              value={`${totalArea.toLocaleString()}`}
              subtitle="square meters"
              color={colors.warning}
              bgColor="#fffbeb"
            />
          </Col>
          <Col lg={3} sm={6}>
            <MetricCard
              icon="ri-calendar-line"
              title="Started"
              value={moment(block?.created_at).format("MMM DD")}
              subtitle={moment(block?.created_at).format("YYYY")}
              color={colors.info}
              bgColor="#f0fdfa"
            />
          </Col>
        </Row>

        <Row className="g-4">
          <Col>
            {/* Detailed Information */}
            <StyledCard className="mb-4">
              <CardHeader className="bg-transparent border-bottom-0 pb-0">
                <SectionHeader 
                  icon="ri-information-line" 
                  title="Block Information" 
                  color={colors.primary} 
                />
              </CardHeader>
              <CardBody className="pt-3">
                <Row className="g-4">
                  <Col lg={6}>
                    <div className="info-section">
                      <h6 className="section-subtitle mb-3">
                        <i className="ri-file-text-line me-2" style={{ color: colors.primary }}></i>
                        Basic Details
                      </h6>
                      <InfoTable data={basicInfoData} />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="info-section">
                      <h6 className="section-subtitle mb-3">
                        <i className="ri-layout-grid-line me-2" style={{ color: colors.success }}></i>
                        Area Breakdown
                      </h6>
                      <div className="area-breakdown">
                        <div className="area-item">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span>Apartment Area</span>
                            <span className="fw-medium">{block?.total_apartment_surface || 0} sqm</span>
                          </div>
                          <Progress 
                            value={totalArea > 0 ? ((block?.total_apartment_surface || 0) / totalArea) * 100 : 0} 
                            color="primary" 
                            className="mb-3"
                            style={{ height: "6px" }}
                          />
                        </div>
                        <div className="area-item">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span>Terrace Area</span>
                            <span className="fw-medium">{block?.total_terrace_surface || 0} sqm</span>
                          </div>
                          <Progress 
                            value={totalArea > 0 ? ((block?.total_terrace_surface || 0) / totalArea) * 100 : 0} 
                            color="success" 
                            className="mb-3"
                            style={{ height: "6px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </StyledCard>

            {/* Timeline Section */}
            <StyledCard>
              <CardHeader className="bg-transparent border-bottom-0 pb-0">
                <SectionHeader 
                  icon="ri-history-line" 
                  title="Project Timeline" 
                  color={colors.warning} 
                />
              </CardHeader>
              <CardBody className="pt-3">
                <div className="timeline-container">
                  <div className="timeline-item" onClick={togglecol1}>
                    <div className="timeline-marker" style={{ backgroundColor: colors.success }}>
                      <i className="ri-play-circle-line"></i>
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-header">
                        <h6 className="timeline-title mb-1">Project Initiated</h6>
                        <span className="timeline-date">{formatDate(block?.created_at)}</span>
                      </div>
                      <Collapse isOpen={col1}>
                        <div className="timeline-details mt-3">
                          <div className="alert alert-light border-0" style={{ backgroundColor: "#f8fafc" }}>
                            <div className="d-flex align-items-start">
                              <i className="ri-information-line me-2 mt-1" style={{ color: colors.success }}></i>
                              <div>
                                <strong>Block construction phase started</strong>
                                <p className="mb-0 mt-1 text-muted small">
                                  Initial planning and foundation work began on {formatDate(block?.created_at)}. 
                                  All permits and approvals are in place for the {block?.total_flats} unit development.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  </div>
                </div>
              </CardBody>
            </StyledCard>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlockDetail;